import { createEvent } from 'effector';
import { track } from './amplitude';
import { CAMERA_SESSION } from './events';
import { $sceneId, sceneReady } from '../state/appState';
import { sceneLoadingReset } from '../state/sceneLoading';
import { setViewMode, $viewMode } from '../state/viewMode';
import type { SceneBindings } from '../scene/createScene';

/**
 * Camera Session — один агрегат на жизненный цикл сцены.
 *
 * Подход к anti-spam'у (RAM-ёмкость ~200 байт на сессию):
 *   - НЕ слушаем per-frame `controls.change` — только pointer/wheel
 *     события на canvas. Жест = pointerdown→up, не каждый frame.
 *   - Дельты вычисляем как snapshot до/после жеста (azimuth, polar,
 *     distance), а не интегрированием.
 *   - Wheel zoom дебаунсится 200 ms — серия колесом = 1 zoom-жест.
 *   - Программные переходы камеры (cluster zoom, navigateToScene,
 *     transitionToPose) не считаются жестами — нет pointerdown.
 *   - Idle (>2s gap между inputs) копится в `totalIdleMs`.
 *
 * Lifecycle:
 *   sceneReady(bindings) → create accumulator, attach listeners
 *   pointerdown/up, wheel → накопление
 *   sceneLoadingReset      → flush + detach (Вариант A: каждая сцена,
 *                             включая inside, — отдельный Camera Session)
 *   pagehide              → flush последней активной сессии
 */

export const cameraPreloaderSkipped = createEvent();

const IDLE_GAP_MS = 2000;
const WHEEL_DEBOUNCE_MS = 200;

type PoseSnapshot = {
  azimuth: number;
  polar: number;
  distance: number;
};

type Accumulator = {
  sceneId: string | null;
  startedAt: number;
  firstInteractionAt: number | null;
  lastActivityAt: number;

  dragGestures: number;
  zoomGestures: number;

  azimuthRotationDeg: number;
  polarRotationDeg: number;
  zoomDistanceDelta: number;

  totalIdleMs: number;
  idlePeriodsCount: number;

  viewModeChanges: number;
  preloaderSkipped: boolean;

  /** Pose в момент pointerdown — используется для дельты на pointerup. */
  gestureStartPose: PoseSnapshot | null;
  /** Pose в начале wheel-серии (до debounce). */
  wheelStartPose: PoseSnapshot | null;
  wheelDebounceTimer: ReturnType<typeof setTimeout> | null;
};

type SessionContext = {
  accumulator: Accumulator;
  bindings: SceneBindings;
  canvas: HTMLCanvasElement;
  pointerDownHandler: (e: PointerEvent) => void;
  pointerUpHandler: (e: PointerEvent) => void;
  wheelHandler: (e: WheelEvent) => void;
};

let current: SessionContext | null = null;

const rad2deg = (rad: number): number => (rad * 180) / Math.PI;

const readPose = (bindings: SceneBindings): PoseSnapshot | null => {
  const camera = bindings.camera;
  const orbit = bindings.controls?.orbit;
  if (!camera || !orbit) return null;
  // Вычисляем sferical-составляющие напрямую из вектора (camera - target),
  // чтобы не плодить экспозицию `getAzimuthalAngle` через wrapper. Метрики
  // одинаково работают в orbit и first-person — pose это всегда положение
  // камеры относительно orbit.target.
  const dx = camera.position.x - orbit.target.x;
  const dy = camera.position.y - orbit.target.y;
  const dz = camera.position.z - orbit.target.z;
  const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (distance < 1e-6) {
    return { azimuth: 0, polar: 0, distance: 0 };
  }
  return {
    azimuth: Math.atan2(dx, dz),
    polar: Math.acos(Math.max(-1, Math.min(1, dy / distance))),
    distance,
  };
};

const angleDelta = (a: number, b: number): number => {
  // Кратчайшая дуга между двумя углами (учёт wrap'а через 2π).
  let d = Math.abs(a - b);
  if (d > Math.PI) d = 2 * Math.PI - d;
  return d;
};

const noteActivity = (acc: Accumulator, now: number): void => {
  const gap = now - acc.lastActivityAt;
  if (gap > IDLE_GAP_MS) {
    acc.totalIdleMs += gap;
    acc.idlePeriodsCount += 1;
  }
  acc.lastActivityAt = now;
  if (acc.firstInteractionAt === null) {
    acc.firstInteractionAt = now;
  }
};

const finalizeGesture = (
  acc: Accumulator,
  start: PoseSnapshot,
  end: PoseSnapshot,
): void => {
  acc.azimuthRotationDeg += rad2deg(angleDelta(start.azimuth, end.azimuth));
  acc.polarRotationDeg += rad2deg(angleDelta(start.polar, end.polar));
  acc.zoomDistanceDelta += Math.abs(end.distance - start.distance);
};

const flushCurrentSession = (): void => {
  if (current === null) return;
  const { accumulator: a, canvas, pointerDownHandler, pointerUpHandler, wheelHandler } = current;

  if (a.wheelDebounceTimer !== null) {
    clearTimeout(a.wheelDebounceTimer);
    a.wheelDebounceTimer = null;
  }

  canvas.removeEventListener('pointerdown', pointerDownHandler);
  canvas.removeEventListener('pointerup', pointerUpHandler);
  canvas.removeEventListener('pointercancel', pointerUpHandler);
  canvas.removeEventListener('wheel', wheelHandler);

  const now = performance.now();
  const durationMs = Math.round(now - a.startedAt);
  const totalIdleMs = Math.round(a.totalIdleMs);
  const interactiveMs = Math.max(0, durationMs - totalIdleMs);

  track(CAMERA_SESSION, {
    scene_id: a.sceneId,
    duration_ms: durationMs,
    first_interaction_ms:
      a.firstInteractionAt === null
        ? null
        : Math.round(a.firstInteractionAt - a.startedAt),
    interactive_ms: interactiveMs,
    drag_gestures: a.dragGestures,
    zoom_gestures: a.zoomGestures,
    azimuth_rotation_deg: Math.round(a.azimuthRotationDeg),
    polar_rotation_deg: Math.round(a.polarRotationDeg),
    zoom_distance_delta: +a.zoomDistanceDelta.toFixed(2),
    idle_periods_count: a.idlePeriodsCount,
    total_idle_ms: totalIdleMs,
    view_mode_changes: a.viewModeChanges,
    view_mode_at_exit: $viewMode.getState(),
    preloader_skipped: a.preloaderSkipped,
  });

  current = null;
};

const startSession = (bindings: SceneBindings): void => {
  // Гарантия: если предыдущая сцена не успела flush'нуться (теоретически
  // невозможно при правильном порядке sceneLoadingReset → sceneReady, но
  // защищаемся), флашим до создания новой.
  if (current !== null) {
    flushCurrentSession();
  }

  const canvas = bindings.renderer?.domElement;
  if (!(canvas instanceof HTMLCanvasElement)) return;

  const now = performance.now();
  const accumulator: Accumulator = {
    sceneId: $sceneId.getState() == null ? null : String($sceneId.getState()),
    startedAt: now,
    firstInteractionAt: null,
    lastActivityAt: now,
    dragGestures: 0,
    zoomGestures: 0,
    azimuthRotationDeg: 0,
    polarRotationDeg: 0,
    zoomDistanceDelta: 0,
    totalIdleMs: 0,
    idlePeriodsCount: 0,
    viewModeChanges: 0,
    preloaderSkipped: false,
    gestureStartPose: null,
    wheelStartPose: null,
    wheelDebounceTimer: null,
  };

  const pointerDownHandler = (): void => {
    if (current === null) return;
    const a = current.accumulator;
    noteActivity(a, performance.now());
    a.gestureStartPose = readPose(current.bindings);
  };

  const pointerUpHandler = (): void => {
    if (current === null) return;
    const a = current.accumulator;
    const start = a.gestureStartPose;
    a.gestureStartPose = null;
    if (start === null) return;
    const end = readPose(current.bindings);
    if (end === null) return;
    finalizeGesture(a, start, end);
    a.dragGestures += 1;
    a.lastActivityAt = performance.now();
  };

  const wheelHandler = (): void => {
    if (current === null) return;
    const a = current.accumulator;
    noteActivity(a, performance.now());
    if (a.wheelStartPose === null) {
      a.wheelStartPose = readPose(current.bindings);
    }
    if (a.wheelDebounceTimer !== null) {
      clearTimeout(a.wheelDebounceTimer);
    }
    a.wheelDebounceTimer = setTimeout(() => {
      if (current === null) return;
      const acc = current.accumulator;
      const start = acc.wheelStartPose;
      acc.wheelStartPose = null;
      acc.wheelDebounceTimer = null;
      if (start === null) return;
      const end = readPose(current.bindings);
      if (end === null) return;
      finalizeGesture(acc, start, end);
      acc.zoomGestures += 1;
    }, WHEEL_DEBOUNCE_MS);
  };

  canvas.addEventListener('pointerdown', pointerDownHandler);
  canvas.addEventListener('pointerup', pointerUpHandler);
  canvas.addEventListener('pointercancel', pointerUpHandler);
  // passive: true — мы не вызываем preventDefault на wheel, читаем только.
  canvas.addEventListener('wheel', wheelHandler, { passive: true });

  current = {
    accumulator,
    bindings,
    canvas,
    pointerDownHandler,
    pointerUpHandler,
    wheelHandler,
  };
};

export const startCameraSessionTracking = (): void => {
  sceneReady.watch((bindings) => {
    startSession(bindings);
  });

  sceneLoadingReset.watch(() => {
    flushCurrentSession();
  });

  setViewMode.watch(() => {
    if (current === null) return;
    current.accumulator.viewModeChanges += 1;
  });

  cameraPreloaderSkipped.watch(() => {
    if (current === null) return;
    current.accumulator.preloaderSkipped = true;
  });

  window.addEventListener('pagehide', () => {
    flushCurrentSession();
  });
};
